import { useEffect, useState } from "react";
import { useGetPhoneCodesQuery } from "../../../entities/preset-data";
import { PhoneCode } from "../../../entities/preset-data/model/types";
import {
  TextField,
  Box,
  InputAdornment,
  Popover,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useMask } from "@react-input/mask";

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
}

export const PhoneInput = ({ onChange }: PhoneInputProps) => {
  const { data: phoneCodes } = useGetPhoneCodesQuery();
  const [selectedCountry, setSelectedCountry] = useState<PhoneCode | null>(
    null
  );
  const [phoneNumber, setPhoneNumber] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [mask, setMask] = useState("");

  // Применение маски к полю ввода
  const maskedInputRef = useMask({
    mask,
    replacement: { _: /\d/ },
    showMask: true,
    separate: true,
  });

  useEffect(() => {
    if (phoneCodes?.countries && phoneCodes.countries.length > 0) {
      const russia = phoneCodes.countries.find(
        (country) => country.phoneCode === "7"
      );
      const countryToUse = russia || phoneCodes.countries[0];
      setSelectedCountry(countryToUse);

      // Установка маски на основе формата из бэкенда
      if (countryToUse?.phoneMask) {
        setMask(countryToUse.phoneMask.replace(/9/g, "_"));
      } else {
        // Маска по умолчанию, если не предоставлена бэкендом
        const defaultMask =
          countryToUse?.phoneCode === "7" ? "___-___-__-__" : "___-___-____";
        setMask(defaultMask);
      }
    }
  }, [phoneCodes]);

  const handleCountryChange = (country: PhoneCode) => {
    setSelectedCountry(country);
    setAnchorEl(null);
    setPhoneNumber("");

    if (country?.phoneMask) {
      setMask(country.phoneMask.replace(/9/g, "_"));
    } else {
      const defaultMask =
        country?.phoneCode === "7" ? "___-___-__-__" : "___-___-____";
      setMask(defaultMask);
    }

    onChange(`+${country.phoneCode}`);

    if (maskedInputRef.current) {
      maskedInputRef.current.focus();
    }
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPhoneNumber = e.target.value;
    setPhoneNumber(newPhoneNumber);

    const digitsOnly = newPhoneNumber.replace(/\D/g, "");
    updateFullPhoneValue(digitsOnly, selectedCountry);
  };

  const updateFullPhoneValue = (number: string, country: PhoneCode | null) => {
    if (country && number) {
      onChange(`+${country.phoneCode}${number}`);
    } else if (country) {
      onChange(`+${country.phoneCode}`);
    } else {
      onChange(number);
    }
  };

  const handleCountryClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setSearchQuery("");
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const filteredCountries =
    phoneCodes?.countries.filter(
      (country) =>
        country.name.toLowerCase().includes(searchQuery) ||
        country.phoneCode.includes(searchQuery)
    ) || [];

  const open = Boolean(anchorEl);

  const isPhoneComplete = () => {
    if (!phoneNumber || !selectedCountry) return false;

    return !phoneNumber.includes("_");
  };

  return (
    <Box sx={{ mb: 3 }}>
      <TextField
        fullWidth
        variant="filled"
        value={phoneNumber}
        onChange={handlePhoneNumberChange}
        inputRef={maskedInputRef}
        placeholder=""
        error={phoneNumber.length > 0 && !isPhoneComplete()}
        InputLabelProps={{
          shrink: false,
        }}
        sx={{
          "& .MuiFilledInput-root": {
            borderRadius: 1,
            paddingLeft: 0,
            border: "none",
            backgroundColor: "#333",
          },
          "& .MuiFilledInput-input": {
            padding: "16px",
            paddingLeft: "15px",
            fontSize: "16px",
          },
          "& .MuiInputAdornment-root": {
            marginRight: 0,
            height: "100%",
          },
          "& .MuiInputLabel-filled": {
            display: "none",
          },
          "& .Mui-focused": {
            backgroundColor: "#333 !important",
          },
          "& .MuiFilledInput-root:hover": {
            backgroundColor: "#333",
          },
        }}
        InputProps={{
          disableUnderline: true,
          startAdornment: (
            <InputAdornment
              position="start"
              sx={{ height: "100%", marginTop: "0 !important" }}
            >
              <Box
                onClick={handleCountryClick}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "#fff",
                  height: "100%",
                  backgroundColor: "transparent",
                  minWidth: "90px",
                  paddingX: 2,
                  position: "relative",
                }}
              >
                <Typography variant="body1" sx={{ fontSize: "16px" }}>
                  +{selectedCountry?.phoneCode || ""}
                </Typography>
                <KeyboardArrowDownIcon fontSize="small" sx={{ ml: 0.5 }} />

                <Divider
                  orientation="vertical"
                  flexItem
                  sx={{
                    position: "absolute",
                    right: 0,
                    height: "24px",
                    borderColor: "rgba(255,255,255,0.15)",
                  }}
                />
              </Box>
            </InputAdornment>
          ),
        }}
      />

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: { width: 320, maxHeight: 400 },
        }}
      >
        <List sx={{ overflow: "auto", maxHeight: 330 }}>
          {filteredCountries.map((country) => (
            <ListItem
              key={country.id}
              onClick={() => handleCountryChange(country)}
              sx={{
                py: 1.5,
                borderBottom: "1px solid rgba(0, 0, 0, 0.08)",
                "&:hover": { bgcolor: "rgba(0, 0, 0, 0.04)" },
                cursor: "pointer",
              }}
            >
              <ListItemText
                primary={
                  <Box
                    sx={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <Typography variant="body1">{country.name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      +{country.phoneCode}
                    </Typography>
                  </Box>
                }
              />
            </ListItem>
          ))}
        </List>
      </Popover>
    </Box>
  );
};
